import { Box, Button } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Control,
  FormState,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
} from "react-hook-form";
import { get, isEmpty } from "lodash";
import { v4 as uuidv4 } from "uuid";

import {
  PanelBox,
  TabsPanelSectionBox,
  PatientHeaderTabs,
  PatientHeaderTab,
  ReferralGrid,
  DetailBox,
  width100PCT,
  CancelButtonGrid,
  NextButtonGrid,
  ButtonMinWidth,
} from "../styles/style";
import {
  BackClickAlertMessage,
  confirmationMessage,
  ReferralRequestHeaderLabel,
  SaveDetailsAlertMessage,
} from "../../../constants/Referral";
import { a11yProps, getValue, isEmptyValue } from "../../../utils";
import { withMatchedPatientState } from "../../../utils/StateAbbreviation";
import ReferralDetails from "../containers/ReferralDetails";
import { TabSessionStorageResponse } from "../../../models/Api/TabSessionStorage";
import { flexAlignCentre } from "../../../styles/mui/styles/display";
import { TabData } from "../../../models/TabSessionStorage";
import ICD10 from "../containers/ICD10";
import ServiceRequest from "../containers/ServiceRequest";
import DocumentUpload from "../containers/DocumentUpload";
import AlertDialog from "./AlertDialog";
import { RequestTabCount } from "../../../constants/ReferralRequest";
import MemberDetails from "../containers/MemberDetails";
import ReviewDetails from "./Review/ReviewDetails";
import ReviewCertify from "./Review/ReviewCertify";
import { EligibilityInformation } from "../../../models/CheckEligibility";
import ConfirmationDialog from "./ConfirmationDialog";
import { CHECK_ELIGIBILITY_WORKFLOW, WELCOME_PAGE } from "../../../constants/ReferralPaths";
import { RequestAuthorizationPayload } from "../../../models/RequestAuthorization";
import { RequestAuthorizationResponse } from "../../../models/Api/RequestAuthorization";
import ErrorDialog from "./ErrorDialog";
import { ReferralRequestorResponse } from "../../../models/Api/ReferralRequest";

export interface PropsFromState {
  setCurrentTab: (id: number) => void;
  TabSessionStorage: TabSessionStorageResponse;
  reset: UseFormReset<TabData>;
  getValues: UseFormGetValues<TabData>;
  setValue: UseFormSetValue<TabData>;
  control: Control<TabData, any>;
  handleSubmit: () => any;
  formState: FormState<any>;
  currentTab: number;
  values: any;
  icd10RecordsFields: any[];
  icd10RecordsAppend: object;
  icd10RecordsRemove: (id: number) => void;
  formError: any;
  eligibilityData: EligibilityInformation | null;
  clearErrors: UseFormClearErrors<TabData>;
  watch: UseFormWatch<TabData>;
  referralSubmitData: RequestAuthorizationResponse;
  referralRequestor: ReferralRequestorResponse;
  unregister: UseFormUnregister<any>;
}
export interface PropsFromDispatch {
  saveTabSessionStorage: (SessionData: TabData) => void;
  resetStates: (actionType: string[]) => void;
  requestAuthorization: (payload: RequestAuthorizationPayload) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  className?: string;
}

function TabsPanelSection(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <TabsPanelSectionBox
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <PanelBox>
          <Box>{children}</Box>
        </PanelBox>
      )}
    </TabsPanelSectionBox>
  );
}

type AllProps = PropsFromState & PropsFromDispatch;

const ReferralRequestTab: React.FC<AllProps> = ({
  setCurrentTab,
  handleSubmit,
  currentTab,
  control,
  setValue,
  reset,
  getValues,
  formState,
  values,
  icd10RecordsAppend,
  icd10RecordsRemove,
  icd10RecordsFields,
  clearErrors,
  watch,
  formError,
  eligibilityData,
  requestAuthorization,
  referralSubmitData,
  referralRequestor,
  resetStates,
  unregister
}: AllProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isNextStep, setIsNextStep] = useState<boolean>(false);
  const [backClick, setBackClick] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [isTabChange, setIsTabChange] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const navigate = useNavigate();
  const [hasFormError, setHasFormError] = useState(false);
  const [isReferralCompleted, setIsReferralCompleted] = useState(false);
  const [errorResponse, setErrorResponse] = useState();

  const eligibilityId = getValue(eligibilityData, "eligibilityId");
  const eligibilityStatus = getValue(eligibilityData, "eligibilityStatus");
  const emergencyContact = getValues("emergencyContact");
  const emergencyContactArray =
    !isEmpty(emergencyContact) && !isEmptyValue(emergencyContact)
      ? [emergencyContact]
      : null;
  const serviceRequest = getValues("serviceRequest");
  const diagnosisCode = getValues("diagnosisCode");
  const referralBlobDetail = getValues("referralBlobDetail");
  const patientDetail = getValues("patientDetail");
  const {
    residentialAddress,
    languageId,
    ...rest // rest of the properties in patientDetail
  } = patientDetail || {};
  // childObject and LanguageID to null
  const updatedPatientDetail = {
    ...rest,
    residentialAddress: eligibilityStatus
      ? null
      : withMatchedPatientState(residentialAddress),
    isManual: eligibilityStatus ? false : true,
    languageId: languageId || null,
  };
  const referralSource = getValues("referralSource");
  const requester = getValues("requester");
  const orderingPhysician = getValues("orderingPhysician");
  const primaryPhysician = getValues("primaryPhysician");
  const primaryPhysicianData =
    !isEmpty(primaryPhysician) && !isEmptyValue(primaryPhysician)
      ? primaryPhysician
      : null;
  const serviceAddress = withMatchedPatientState(getValues("serviceAddress"));
  const serviceProvider = getValues("serviceProvider");
  const { response: userDetail } = referralRequestor;

  useEffect(() => {
    if (currentTab == RequestTabCount) {
      setCurrentTab(activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    const formErrorLength = !isEmpty(get(formError, "response.SessionData", {}))
      ? Object.keys(get(formError, "response.SessionData", {})).length
      : 0;
    if (
      isTabChange &&
      formError &&
      !formError.loading &&
      formError.error &&
      Object.keys(formError.error).length == 0 &&
      formErrorLength >= 0
    ) {
      if (currentTab < RequestTabCount && currentTab != RequestTabCount) {
        setCurrentTab(activeTab);
        setOpenAlert(false);
      }
      if (currentTab == RequestTabCount) {
        setCurrentTab(activeTab);
        setOpenAlert(false);
      }
      setIsTabChange(false);
    } else if (
      isNextStep &&
      formError &&
      !formError.loading &&
      formError.error &&
      Object.keys(formError.error).length == 0 &&
      formErrorLength >= 0
    ) {
      if (currentTab < RequestTabCount && currentTab != RequestTabCount) {
        setCurrentTab(currentTab + 1);
      }
      if (currentTab == RequestTabCount) {
        setCurrentTab(activeTab);
      }
      setIsNextStep(false);
    }
  }, [formError]);

  useEffect(() => {
    if (referralSubmitData.response?.referralId && !hasFormError) {
      sessionStorage.setItem(
        "referralId",
        referralSubmitData.response?.referralId
      );
      setHasFormError(false);
      setOpenConfirmation(true);
      setIsReferralCompleted(false);
    } else if (isReferralCompleted) {
      setHasFormError(true);
      setErrorResponse(referralSubmitData.response);
    }
  }, [referralSubmitData.response]);

  useEffect(()=>{
    if(!isEmpty(sessionStorage.getItem("referralId"))){
      navigate(WELCOME_PAGE);
    }
  },[])

  const handleChange = async (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    if (currentTab != RequestTabCount) {
      setOpenAlert(true);
    }
    setActiveTab(newValue);
  };

  const handleNextStep = () => {
    handleSubmit();
    setIsTabChange(false);
    setIsNextStep(true);
  };

  const handleBackStep = () => {
    if (currentTab == RequestTabCount) {
      setCurrentTab(currentTab - 1);
    } else {
      setOpenAlert(true);
      setBackClick(true);
    }
  };

  const handleSave = () => {
    handleSubmit();
    setIsTabChange(true);
    setOpenAlert(false);
  };

  const handleBackClick = () => {
    setOpenAlert(false);
    setBackClick(false);
    if (currentTab != 0) {
      setCurrentTab(currentTab - 1);
    }
  };

  const handleSubmitReferral = () => {
    const payload = {
      eligibilityId: eligibilityId || 0,
      sessionId: uuidv4(),
      userDetail: userDetail || null,
      patientDetail: updatedPatientDetail || null,
      emergencyContact: emergencyContactArray,
      referralSource: referralSource || null,
      requester: requester || null,
      orderingPhysician: orderingPhysician || null,
      primaryPhysician: primaryPhysicianData || null,
      serviceAddress: serviceAddress || null,
      serviceProvider: serviceProvider || null,
      diagnosisCode: diagnosisCode || null,
      serviceRequest: serviceRequest || null,
      referralBlobDetail: referralBlobDetail || null,
    };

    const updatedPayload = {
      ...payload,
      reviewDetail: {
        FToFEncounterDate: getValues("reviewDetail.FToFEncounterDate") || null,
        ProviderName: getValues("reviewDetail.ProviderName"),
        medicaidMedicareChecked: getValues(
          "reviewDetail.medicaidMedicareChecked"
        ),
      },
    };

    setIsReferralCompleted(true);
    requestAuthorization(updatedPayload);
  };

  const handleCancel = () => {
    sessionStorage.removeItem("ReferralRequest");
    navigate(CHECK_ELIGIBILITY_WORKFLOW);
  };

  return (
    <DetailBox>
      <Box sx={width100PCT}>
        <Grid container sx={ReferralGrid}>
          <Grid size={{ xs: 12 }}>
            <Box>
              <Box>
                <PatientHeaderTabs
                  value={currentTab}
                  onChange={handleChange}
                  aria-label="Patient Header Tabs"
                >
                  {ReferralRequestHeaderLabel.map((data, index) => (
                    <PatientHeaderTab
                      key={data.label}
                      label={data.label}
                      {...a11yProps(index)}
                    />
                  ))}
                </PatientHeaderTabs>
              </Box>
              <TabsPanelSection value={currentTab} index={0}>
                <MemberDetails
                  control={control}
                  reset={reset}
                  setValue={setValue}
                  getValues={getValues}
                  eligibilityData={eligibilityData}
                />
              </TabsPanelSection>
              <TabsPanelSection value={currentTab} index={1}>
                <ReferralDetails
                  control={control}
                  reset={reset}
                  setValue={setValue}
                  getValues={getValues}
                  clearErrors={clearErrors}
                  watch={watch}
                  unregister={unregister}
                  patientDetail={patientDetail}
                />
              </TabsPanelSection>
              <TabsPanelSection value={currentTab} index={2}>
                <ICD10
                  icdvalues={values}
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  fields={icd10RecordsFields}
                  append={icd10RecordsAppend}
                  remove={icd10RecordsRemove}
                  reset={reset}
                  clearErrors={clearErrors}
                  watch={watch}
                  formState={formState}
                />
              </TabsPanelSection>
              <TabsPanelSection value={currentTab} index={3}>
                <ServiceRequest
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  eligibilityData={eligibilityData}
                  formState={formState}
                />
              </TabsPanelSection>
              <TabsPanelSection value={currentTab} index={4}>
                <DocumentUpload setValue={setValue} control={control} />
              </TabsPanelSection>
              <TabsPanelSection value={currentTab} index={5}>
                <ReviewDetails getValues={getValues} />
              </TabsPanelSection>
              <TabsPanelSection value={currentTab} index={5}>
                <ReviewCertify control={control} />
              </TabsPanelSection>
            </Box>
          </Grid>
        </Grid>
        <Grid container sx={flexAlignCentre}>
          <Grid size={{ xs: 6 }} sx={CancelButtonGrid}>
            <Button
              variant="outlined"
              sx={ButtonMinWidth}
              onClick={handleCancel}
            >
              CANCEL
            </Button>
            {currentTab === RequestTabCount && (
              <Button
                variant="outlined"
                sx={ButtonMinWidth}
                onClick={handleBackStep}
              >
                PREVIOUS
              </Button>
            )}
          </Grid>
          <Grid size={{ xs: 6 }} sx={NextButtonGrid}>
            {currentTab !== RequestTabCount ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleBackStep}
                  disabled={currentTab === 0}
                  sx={ButtonMinWidth}
                >
                  PREVIOUS
                </Button>
                <Button
                  variant="contained"
                  sx={ButtonMinWidth}
                  onClick={handleNextStep}
                >
                  NEXT
                </Button>
              </>
            ) : (
              <Button onClick={handleSubmitReferral} variant="contained">
                REQUEST AUTHORIZATIONS
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
      <AlertDialog
        alertMessage={
          backClick ? BackClickAlertMessage : SaveDetailsAlertMessage
        }
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        handleSubmit={handleSave}
        back={backClick}
        handleBackClick={handleBackClick}
      />
      {openConfirmation && (
        <ConfirmationDialog
          confirmationMessageTitle={confirmationMessage.title}
          openConfirmation={openConfirmation}
          setOpenConfirmation={setOpenConfirmation}
          resetStates={resetStates}
        />
      )}
      {hasFormError && (
        <ErrorDialog
          hasFormError={hasFormError}
          setHasFormError={setHasFormError}
          formError={errorResponse}
        />
      )}
    </DetailBox>
  );
};

export default ReferralRequestTab;
